import { UmsMember, UmsMemberLoginLog } from 'types/ums';
import LOCALS from 'commons/locals';
import { Trans } from 'react-i18next';
import { Card, Table } from 'antd';
import usePagination from 'commons/hooks/usePagination';
import { useCallback, useEffect, useMemo } from 'react';
import { PageQuery } from 'types/base';
import { getMemberLoginLogList } from 'apis/ums';
import { ColumnsType } from 'antd/es/table';
import showTotal from 'components/show-total';
import formatTime from 'utils/formatTime';
import { LogIn } from 'lucide-react';

type MemberLoginLogProps = {
  memberId: UmsMember['id'];
};

const LOGIN_TYPE_LABELS: Record<number, string> = {
  1: '官网',
  2: 'App',
};

const LOGIN_METHOD_LABELS: Record<number, string> = {
  1: '账号密码',
  2: '邮件验证码',
  3: 'Google 登录',
  4: '注册激活码',
};

const MemberLoginLog = ({ memberId }: MemberLoginLogProps) => {
  const {
    loading,
    setLoading,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    total,
    setTotal,
    dataSource,
    setDataSource,
  } = usePagination<UmsMemberLoginLog>();

  const getDataSource = useCallback(
    async ({ pageNum, pageSize }: PageQuery) => {
      try {
        setLoading(true);
        const { list, total } = await getMemberLoginLogList({
          memberId,
          pageNum,
          pageSize,
        });

        setDataSource(list);
        setTotal(total);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    },
    [memberId, setDataSource, setLoading, setTotal]
  );

  useEffect(() => {
    getDataSource({ pageNum: 1, pageSize: 10 });
  }, [getDataSource]);

  const columns: ColumnsType<UmsMemberLoginLog> = useMemo(
    () => [
      {
        title: <Trans i18nKey={LOCALS.login_type} />,
        dataIndex: 'loginType',
        key: 'loginType',
        render: (loginType: number) =>
          LOGIN_TYPE_LABELS[loginType] ?? loginType ?? '-',
      },
      {
        title: <Trans i18nKey={LOCALS.login_method} />,
        dataIndex: 'loginMethod',
        key: 'loginMethod',
        render: (loginMethod: number) =>
          LOGIN_METHOD_LABELS[loginMethod] ?? loginMethod ?? '-',
      },
      {
        title: <Trans i18nKey={LOCALS.login_ip} />,
        dataIndex: 'ip',
        key: 'ip',
        render: (ip: string | null) => ip || '-',
      },
      {
        title: <Trans i18nKey={LOCALS.country} />,
        dataIndex: 'country',
        key: 'country',
        render: (country: string | null) => country || '-',
      },
      {
        title: <Trans i18nKey={LOCALS.city} />,
        dataIndex: 'city',
        key: 'city',
        render: (city: string | null) => city || '-',
      },
      {
        title: <Trans i18nKey={LOCALS.created_time} />,
        dataIndex: 'createTime',
        key: 'createTime',
        render: (createTime: string) => formatTime(createTime),
      },
    ],
    []
  );

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <LogIn className="w-5 h-5" />
        <Trans i18nKey={LOCALS.member_login_history} />
      </div>

      <Table
        bordered
        pagination={{
          showTotal,
          total,
          pageSize,
          current: pageNum,
          onChange: (page, pageSize) => {
            setPageNum(page);
            setPageSize(pageSize);
            getDataSource({ pageNum: page, pageSize });
          },
        }}
        loading={loading}
        rowKey="id"
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        dataSource={dataSource}
        columns={columns}
      />
    </Card>
  );
};

export default MemberLoginLog;
